/**
 * Menu — a small popover menu with roving focus (ArrowUp/Down, Home/End,
 * Escape). Items are plain buttons; the caller renders anything else inline.
 * Inline groups of menuitemradios (colour swatches, stage type) are one stop
 * in the arrow order and handle Left/Right themselves; Tab moves within the
 * menu and the menu closes only when focus actually leaves it.
 */
import { useEffect, useRef, type ReactNode } from 'react';

const STOP = '[role="menuitem"]:not([disabled]), [role="group"], [role="radiogroup"]';
const GROUP = '[role="group"], [role="radiogroup"]';
const RADIO = '[role="menuitemradio"], [role="radio"]';

export function Menu({
  open,
  onClose,
  label,
  children,
  align = 'right',
  className = '',
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  children: ReactNode;
  align?: 'left' | 'right';
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const root = ref.current;
    if (!root) return;
    const opener = document.activeElement as HTMLElement | null;
    const stops = () => Array.from(root.querySelectorAll<HTMLElement>(STOP));
    const focusStop = (el: HTMLElement | undefined) => {
      if (!el) return;
      if (el.matches(GROUP)) {
        (el.querySelector<HTMLElement>('[aria-checked="true"]') ?? el.querySelector<HTMLElement>(RADIO))?.focus();
      } else el.focus();
    };
    focusStop(stops()[0]);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        opener?.focus();
        return;
      }
      const target = e.target as HTMLElement | null;
      const inGroup = target?.closest(GROUP);
      if (inGroup && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) return;
      const list = stops();
      if (list.length === 0) return;
      const idx = list.findIndex((el) => el === target || el.contains(target));
      const go = (n: number) => {
        e.preventDefault();
        focusStop(list[(n + list.length) % list.length]);
      };
      if (e.key === 'ArrowDown') go(idx + 1);
      else if (e.key === 'ArrowUp') go(idx - 1);
      else if (e.key === 'Home') go(0);
      else if (e.key === 'End') go(list.length - 1);
    };
    const onDown = (e: MouseEvent) => {
      if (!root.parentElement?.contains(e.target as Node)) onCloseRef.current();
    };
    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (next && !root.contains(next)) onCloseRef.current();
    };
    root.addEventListener('keydown', onKey);
    root.addEventListener('focusout', onFocusOut);
    document.addEventListener('mousedown', onDown);
    return () => {
      root.removeEventListener('keydown', onKey);
      root.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  if (!open) return null;
  return (
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      className={`dropdown-shadow absolute top-full z-40 mt-1 min-w-[200px] rounded-lg bg-white p-1 ${
        align === 'right' ? 'right-0' : 'left-0'
      } ${className}`}
    >
      {children}
    </div>
  );
}

export function MenuItem({
  onSelect,
  children,
  danger = false,
  disabled = false,
}: {
  onSelect: () => void;
  children: ReactNode;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onSelect}
      className={[
        'flex min-h-10 w-full items-center gap-2 rounded-md px-2.5 text-left text-[13px]',
        danger ? 'text-danger hover:bg-danger-bg' : 'text-gray-800 hover:bg-page',
        disabled ? 'opacity-40 cursor-not-allowed' : '',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
